import type { CSSProperties } from 'react'
import type { CameraInfo } from '@/types'
import styles from './nodeDetail.module.css'
import { cx, normalizedRecordingStatus } from './utils'

interface CameraPanelProps {
  cameras: CameraInfo[]
  canControlRecording: boolean
  supportsRecordingControl: boolean
  supportsInferenceRecordingControl?: boolean
  onStartRecording: (key: string) => void
  onStopRecording: (key: string) => void
}

export function CameraPanel({
  cameras,
  canControlRecording,
  supportsRecordingControl,
  supportsInferenceRecordingControl = false,
  onStartRecording,
  onStopRecording,
}: CameraPanelProps) {
  function normalizedStatus(status: CameraInfo['recording_status']) {
    return normalizedRecordingStatus(status)
  }

  function statusLabel(status: CameraInfo['recording_status']) {
    const normalized = normalizedStatus(status)
    if (normalized === 'local_running') return 'Local Running'
    return normalized.charAt(0).toUpperCase() + normalized.slice(1)
  }

  function statusClass(status: CameraInfo['recording_status']) {
    const normalized = normalizedStatus(status)
    if (normalized === 'running') return styles.recordingStatusRunning
    if (normalized === 'local_running') return styles.recordingStatusLocalRunning
    if (normalized === 'starting' || normalized === 'stopping') return styles.recordingStatusPending
    if (normalized === 'failed' || normalized === 'unknown') return styles.recordingStatusFailed
    return styles.recordingStatusIdle
  }

  function actionButtonVars(color: string): CSSProperties {
    return { '--camera-action-color': color } as CSSProperties
  }

  return (
    <div className={styles.card}>
      <div className={styles.cameraPanelHeader}>
        <div className={cx(styles.cardTitle, styles.cardTitleInline)}>Cameras ({cameras.length})</div>
        {!supportsRecordingControl && (
          <span className={styles.agentUpdateRequired}>Agent update required</span>
        )}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.cameraTable}>
          <thead>
            <tr>
              {[
                'Key',
                'Name',
                'Source',
                'Manual recording',
                ...(canControlRecording ? ['Manual actions'] : []),
                'Inference recording',
                ...(canControlRecording ? ['Inference actions'] : []),
              ].map((h) => (
                <th key={h} className={styles.tableHeaderCell}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cameras.map((cam) => {
              const normalized = normalizedStatus(cam.recording_status)
              const busy = normalized !== 'idle'
              const canStop = normalized === 'running'
              const isStopping = normalized === 'stopping'
              const disabledStart = !supportsRecordingControl || busy
              const disabledStop = !supportsRecordingControl || !canStop
              const disabledInference = !supportsInferenceRecordingControl
              return (
                <tr key={cam.key}>
                  <td className={styles.cameraKeyCell}>{cam.key}</td>
                  <td className={styles.cameraNameCell}>{cam.name ?? '—'}</td>
                  <td className={styles.cameraSourceCell}>{cam.source_preview ?? '—'}</td>
                  <td className={styles.cameraCell}>
                    <span
                      title={cam.recording_pid ? `PID ${cam.recording_pid}` : undefined}
                      className={cx(styles.recordingStatusBadge, statusClass(cam.recording_status))}
                    >
                      {statusLabel(cam.recording_status)}
                    </span>
                  </td>
                  {canControlRecording && (
                    <td className={styles.cameraCell}>
                      <div className={styles.cameraActionGroup}>
                        <button
                          onClick={() => onStartRecording(cam.key)}
                          disabled={disabledStart}
                          className={styles.cameraActionButton}
                          style={actionButtonVars('#3949ab')}
                        >
                          Record
                        </button>
                        <button
                          onClick={() => onStopRecording(cam.key)}
                          disabled={disabledStop}
                          title={normalized === 'local_running' ? 'Started outside Node Manager' : undefined}
                          className={styles.cameraActionButton}
                          style={actionButtonVars('#c62828')}
                        >
                          {isStopping ? 'Stopping' : 'Stop'}
                        </button>
                      </div>
                    </td>
                  )}
                  <td className={styles.cameraCell}>
                    <span
                      title="Inference recording control is not implemented yet"
                      className={styles.inferenceUnavailableBadge}
                    >
                      Not implemented
                    </span>
                  </td>
                  {canControlRecording && (
                    <td className={styles.cameraCell}>
                      <div className={styles.cameraActionGroup}>
                        <button
                          disabled={disabledInference}
                          title="Inference recording control is not implemented yet"
                          className={styles.cameraActionButton}
                          style={actionButtonVars('#3949ab')}
                        >
                          Record
                        </button>
                        <button
                          disabled={disabledInference}
                          title="Inference recording control is not implemented yet"
                          className={styles.cameraActionButton}
                          style={actionButtonVars('#c62828')}
                        >
                          Stop
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
